import {
  callbackDataFromUrl,
  parseImportCallback,
  importAuthMessages,
} from "@components/Onboarding/importAuth";
import { toast } from "sonner-native";
import { clearImportSession } from "@components/Onboarding/importSession";
import React, { useRef, useState } from "react";
import { ActivityIndicator, Platform } from "react-native";
import * as WebBrowser from "expo-web-browser";
import { router } from "expo-router";
import LBox from "@components/LBox";
import LText from "@components/LText";
import LIcon, { IconName } from "@components/LIcon";
import LPressable from "@components/LPressable";
import {
  useGetConnectedPlatforms,
  useMusicSyncGetAuthUrl,
  useMusicSyncGetPlaylistSyncProgress,
} from "@hooks/trpc/useMusicSync";
import { useOnboardingStore } from "@components/Onboarding/store/onboardingStore";
import { StreamingService } from "@components/Onboarding/constants";

const DSP_LIST: {
  type: StreamingService;
  name: string;
  icon: IconName;
}[] = [
  { type: "spotify", name: "Spotify", icon: "spotify" },
  { type: "appleMusic", name: "Apple Music", icon: "apple_music" },
  { type: "soundCloud", name: "SoundCloud", icon: "soundcloud" },
];

export default function ConnectedServices() {
  const pending = useRef(false);
  const [busy, setBusy] = useState(false);
  const { data: connectedData } = useGetConnectedPlatforms();
  const { mutateAsync: getAuthUrl } = useMusicSyncGetAuthUrl();
  const syncProgress = useMusicSyncGetPlaylistSyncProgress();

  const connectedPlatforms = new Set(
    connectedData?.platforms.map((p) => p.platform) ?? [],
  );

  const importingPlatform =
    syncProgress &&
    (syncProgress.status === "pending" || syncProgress.status === "running")
      ? syncProgress.platform
      : undefined;

  const handleImport = async (service: StreamingService) => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    try {
      useOnboardingStore.getState().setJourney("direct-import");

      const returnUrl = `${Platform.OS === "web" ? window.location.origin : "lsn:/"}/onboarding/callback`;

      await clearImportSession();
      const { authUrl } = await getAuthUrl({ service, returnUrl });

      if (Platform.OS === "web") {
        // navigating away from page
        window.location.assign(authUrl);
        return;
      }

      const res = await WebBrowser.openAuthSessionAsync(authUrl, returnUrl);
      if (res.type === "success") {
        const data64 = callbackDataFromUrl(res.url, returnUrl);
        const callback = parseImportCallback(data64, service);
        if (!callback.ok) {
          toast.error(importAuthMessages[callback.code]);
          return;
        }
        router.replace({
          pathname: "/onboarding/callback",
          params: { data64: data64! },
        });
      } else {
        toast.error(
          importAuthMessages[
            res.type === "cancel" ? "user_cancelled" : "browser_dismissed"
          ],
        );
      }
    } catch {
      toast.error("We couldn’t connect. Please try again.");
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };

  return (
    <LBox mt="m1">
      <LText variant="bodyM2" color="listItemHeaderText" mb="s3">
        Playlist Import
      </LText>
      <LBox gap="s2">
        {DSP_LIST.map((dsp) => {
          const isConnected = connectedPlatforms.has(dsp.type);
          const isImporting = importingPlatform === dsp.type;
          const isDisabled =
            busy || (!!importingPlatform && !isImporting && !isConnected);
          return (
            <DSPRow
              key={dsp.type}
              dsp={dsp}
              isConnected={isConnected}
              isImporting={isImporting}
              isDisabled={isDisabled}
              onPress={
                isImporting
                  ? () => router.navigate("/(app)/(tabs)/library")
                  : () => handleImport(dsp.type)
              }
            />
          );
        })}
      </LBox>
    </LBox>
  );
}

function DSPRow({
  dsp,
  isConnected,
  isImporting,
  isDisabled,
  onPress,
}: {
  dsp: { type: StreamingService; name: string; icon: IconName };
  isConnected: boolean;
  isImporting: boolean;
  isDisabled: boolean;
  onPress: () => void;
}) {
  const content = (
    <LBox
      flexDirection="row"
      alignItems="center"
      px="s4"
      py="s3"
      borderRadius="m"
      backgroundColor="softNight"
      borderWidth={1}
      borderColor="whiteTranslucent10"
      opacity={isDisabled ? 0.4 : 1}
    >
      <LIcon name={dsp.icon} size={28} />
      <LText variant="bodyM2" color="white" ml="s3" flex={1}>
        {dsp.name}
      </LText>
      {isImporting ? (
        <LBox flexDirection="row" alignItems="center" gap="s2">
          <ActivityIndicator size="small" />
          <LText variant="bodyM" color="whiteTranslucent50">
            Importing...
          </LText>
        </LBox>
      ) : isConnected ? (
        <LIcon name="check" size={20} />
      ) : (
        <LText variant="bodyM" color="whiteTranslucent50">
          Import
        </LText>
      )}
    </LBox>
  );

  if (isConnected && !isImporting) {
    return content;
  }

  if (isDisabled) {
    return content;
  }

  return (
    <LPressable sentryEventName={`import_${dsp.type}`} onPress={onPress}>
      {content}
    </LPressable>
  );
}
